import {
  PLANETS,
  SBC_NAKSHATRAS_28,
  SBC_PERIMETER,
  SIGNS,
} from '../constants.js';
import { createDisclaimerElement } from '../disclaimer.js';
import { renderTransitWheel } from './transit-wheel.js';
import {
  clearElement,
  createTitle,
  enrichPlanetList,
  formatDegreeInSign,
  htmlElement,
  svgElement,
} from '../utils.js';

const SIZE = 900;
const GRID_ORIGIN = 45;
const GRID_SIZE = 810;
const CELL = GRID_SIZE / 9;

const CORNERS = Object.freeze([
  { row: 0, col: 0, label: 'N', description: 'North-west corner' },
  { row: 0, col: 8, label: 'E', description: 'North-east corner' },
  { row: 8, col: 8, label: 'S', description: 'South-east corner' },
  { row: 8, col: 0, label: 'W', description: 'South-west corner' },
]);

// A practical, editable inner grid. Replace this through snapshot.sarvatobhadra.innerCells
// when the client's exact Sanskrit/Hindi akshara, tithi and weekday table is supplied.
const DEFAULT_INNER_GRID_EN = Object.freeze([
  ['A', 'Aa', 'I', 'Ee', 'U', 'Oo', 'Ri'],
  ['E', 'Ai', 'O', 'Au', 'An', 'Ah', 'Swar'],
  ['Ari', 'Tau', 'Gem', 'Can', 'Leo', 'Vir', 'Lib'],
  ['Sco', 'Sag', 'Cap', 'Aqu', 'Pis', 'Tithi', 'Vara'],
  ['Ka', 'Cha', 'Ta', 'Tha', 'Pa', 'Ya', 'Sha'],
  ['Ga', 'Ja', 'Da', 'Dha', 'Ba', 'Ra', 'Sa'],
  ['Letters', 'Signs', 'Days', 'Padas', 'Vedha', 'Latta', 'Result'],
]);

const DEFAULT_INNER_GRID_HI = Object.freeze([
  ['अ', 'आ', 'इ', 'ई', 'उ', 'ऊ', 'ऋ'],
  ['ए', 'ऐ', 'ओ', 'औ', 'अं', 'अः', 'स्वर'],
  ['मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या', 'तुला'],
  ['वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन', 'तिथि', 'वार'],
  ['क', 'च', 'ट', 'त', 'प', 'य', 'श'],
  ['ग', 'ज', 'ड', 'द', 'ब', 'र', 'स'],
  ['अक्षर', 'राशियां', 'दिन', 'पाद', 'वेध', 'लत्ता', 'फल'],
]);

const SBC_NAKSHATRAS_HI = Object.freeze([
  'कृत्तिका', 'रोहिणी', 'मृगशीर्ष', 'आर्द्रा', 'पुनर्वसु', 'पुष्य', 'आश्लेषा',
  'मघा', 'पूर्व फाल्गुनी', 'उत्तर फाल्गुनी', 'हस्त', 'चित्रा', 'स्वाती', 'विशाखा',
  'अनुराधा', 'ज्येष्ठा', 'मूल', 'पूर्वाषाढ़ा', 'उत्तराषाढ़ा', 'अभिजित', 'श्रवण',
  'धनिष्ठा', 'शतभिषा', 'पूर्व भाद्रपदा', 'उत्तर भाद्रपदा', 'रेवती', 'अश्विनी', 'भरणी',
]);

export function renderSarvatobhadra(container, snapshot) {
  clearElement(container);
  const model = normalizeModel(snapshot);
  const layout = htmlElement('div', { className: 'sbc-layout' });
  const visuals = htmlElement('div', { className: 'sbc-visuals' });
  const chartHost = htmlElement('div', { className: 'sbc-chart-host' });
  const wheelHost = htmlElement('section', { className: 'sbc-wheel-host' });
  const svg = svgElement('svg', {
    viewBox: `0 0 ${SIZE} ${SIZE}`,
    className: 'aat-chart aat-sarvatobhadra',
    role: 'img',
    'aria-label': 'Sarvatobhadra Chakra with nakshatras and transit planets',
  });

  svg.append(svgElement('rect', {
    x: 12,
    y: 12,
    width: SIZE - 24,
    height: SIZE - 24,
    rx: 18,
    className: 'sbc-background',
  }));

  drawCells(svg, model);
  drawVedhaLines(svg, model);
  drawPlanetBadges(svg, model);
  drawCenterCaption(svg, snapshot, model);

  const chartViewport = htmlElement('div', { className: 'sbc-chart-viewport', 'data-zoom': '100' });
  const zoomControls = renderZoomControls(chartViewport);
  chartViewport.append(svg);
  chartHost.append(renderPlanetLegend(), zoomControls, chartViewport);
  wheelHost.append(htmlElement('h3', {
    text: model.language === 'hi' ? 'भीतरी: जन्म कुंडली - बाहरी: गोचर' : 'Inner: Birth chart - Outer: Gochar',
  }));
  const wheelCanvas = htmlElement('div', { className: 'sbc-wheel-canvas' });
  renderTransitWheel(wheelCanvas, snapshot, { maxAspectLines: 36 });
  wheelHost.append(wheelCanvas);

  visuals.append(chartHost, wheelHost);
  layout.append(visuals, renderSarvatobhadraDetails(model, snapshot));
  container.append(layout);
  return svg;
}

function renderZoomControls(chartViewport) {
  const levels = [60, 80, 100, 125, 150, 200];
  let levelIndex = levels.indexOf(100);
  const controls = htmlElement('div', { className: 'sbc-zoom-controls', role: 'group', 'aria-label': 'Chart zoom' });
  const zoomOut = htmlElement('button', {
    type: 'button',
    className: 'sbc-zoom-button',
    text: '-',
    title: 'Zoom out',
    'aria-label': 'Zoom out',
  });
  const reset = htmlElement('button', {
    type: 'button',
    className: 'sbc-zoom-button sbc-zoom-reset',
    text: '100%',
    title: 'Fit chart',
    'aria-label': 'Fit chart',
  });
  const zoomIn = htmlElement('button', {
    type: 'button',
    className: 'sbc-zoom-button',
    text: '+',
    title: 'Zoom in',
    'aria-label': 'Zoom in',
  });

  const applyZoom = () => {
    const level = levels[levelIndex];
    chartViewport.dataset.zoom = String(level);
    reset.textContent = `${level}%`;
    zoomOut.disabled = levelIndex === 0;
    zoomIn.disabled = levelIndex === levels.length - 1;
  };
  zoomOut.addEventListener('click', () => {
    levelIndex = Math.max(0, levelIndex - 1);
    applyZoom();
  });
  reset.addEventListener('click', () => {
    levelIndex = levels.indexOf(100);
    applyZoom();
  });
  zoomIn.addEventListener('click', () => {
    levelIndex = Math.min(levels.length - 1, levelIndex + 1);
    applyZoom();
  });
  applyZoom();
  controls.append(zoomOut, reset, zoomIn);
  return controls;
}

function renderPlanetLegend() {
  return htmlElement('div', { className: 'sbc-planet-legend', role: 'note' }, [
    htmlElement('span', { className: 'sbc-legend-item' }, [
      htmlElement('span', { className: 'sbc-legend-badge natal', text: 'Mo' }),
      htmlElement('span', { text: 'Dashed = Natal planet' }),
    ]),
    htmlElement('span', { className: 'sbc-legend-item' }, [
      htmlElement('span', { className: 'sbc-legend-badge transit', text: 'Mo' }),
      htmlElement('span', { text: 'Solid = Gochar planet' }),
    ]),
  ]);
}

function normalizeModel(snapshot) {
  const source = snapshot?.sarvatobhadra || {};
  const language = String(snapshot?.meta?.disclaimerLanguage || 'en').toLowerCase().startsWith('hi') ? 'hi' : 'en';
  const model = {
    ...source,
    language,
    natalPlanets: enrichPlanetList(source.natalPlanets || snapshot?.natal?.planets || []),
    transitPlanets: enrichPlanetList(source.transitPlanets || snapshot?.transit?.planets || []),
    innerCells: normalizeInnerCells(source.innerCells, language),
    referenceCells: new Set(),
    affectedCells: new Set(),
    vedhas: Array.isArray(source.vedhas) ? source.vedhas : [],
    gocharScore: Number.isFinite(Number(source.gocharScore)) ? Number(source.gocharScore) : null,
    trend: source.trend || null,
    ruleContributions: Array.isArray(source.ruleContributions) ? source.ruleContributions : [],
  };

  model.referenceCells = normalizeCellSet(source.referenceCells, model);
  model.affectedCells = normalizeCellSet(source.affectedCells, model);
  return model;
}

function normalizeCellSet(references, model) {
  return new Set((Array.isArray(references) ? references : [])
    .map((reference) => normalizeCellReference(reference, model))
    .filter(Boolean)
    .map(cellKey));
}

function normalizeInnerCells(cells, language) {
  if (!Array.isArray(cells) || cells.length !== 7) {
    const defaultGrid = language === 'hi' ? DEFAULT_INNER_GRID_HI : DEFAULT_INNER_GRID_EN;
    return defaultGrid.flatMap((row, rowIndex) => row.map((label, colIndex) => ({
      row: rowIndex + 1,
      col: colIndex + 1,
      label,
      kind: classifyDefaultLabel(label),
    })));
  }

  return cells.flatMap((row, rowIndex) => {
    if (!Array.isArray(row) || row.length !== 7) {
      throw new Error('sarvatobhadra.innerCells must be a 7 × 7 array.');
    }
    return row.map((value, colIndex) => {
      const object = typeof value === 'object' && value !== null ? value : { label: value };
      return {
        row: rowIndex + 1,
        col: colIndex + 1,
        label: object.label ?? '',
        kind: object.kind || 'inner',
        description: object.description || '',
      };
    });
  });
}

function classifyDefaultLabel(label) {
  if (SIGNS.some((sign) => sign.short === label)) return 'sign';
  if (/^[अ-ह]/u.test(label)) return 'akshara';
  return 'category';
}

function drawCells(svg, model) {
  const group = svgElement('g', { className: 'sbc-grid' });

  for (let row = 0; row < 9; row += 1) {
    for (let col = 0; col < 9; col += 1) {
      const key = `${row}:${col}`;
      const perimeterIndex = SBC_PERIMETER.findIndex((position) => position.row === row && position.col === col);
      const corner = CORNERS.find((position) => position.row === row && position.col === col);
      const inner = model.innerCells.find((cell) => cell.row === row && cell.col === col);
      const classes = ['sbc-cell'];
      if (perimeterIndex >= 0) classes.push('sbc-cell-nakshatra');
      if (corner) classes.push('sbc-cell-corner');
      if (inner) classes.push(`sbc-cell-${inner.kind || 'inner'}`);
      if (model.referenceCells.has(key)) classes.push('is-reference');
      if (model.affectedCells.has(key)) classes.push('is-affected');

      const x = GRID_ORIGIN + col * CELL;
      const y = GRID_ORIGIN + row * CELL;
      const cellGroup = svgElement('g', {
        className: classes.join(' '),
        'data-cell': key,
      });
      cellGroup.append(svgElement('rect', {
        x,
        y,
        width: CELL,
        height: CELL,
        className: 'sbc-cell-rect',
      }));

      if (perimeterIndex >= 0) {
        const label = localizedNakshatra(SBC_NAKSHATRAS_28[perimeterIndex], model.language);
        const fontSize = model.language === 'hi' && label.length > 9 ? 11 : 14;
        const text = makeWrappedText(label, x + CELL / 2, y + CELL * 0.28, fontSize, 2);
        text.setAttribute('class', 'sbc-nakshatra-label');
        cellGroup.append(text, svgElement('text', {
          x: x + CELL / 2,
          y: y + CELL * 0.58,
          className: 'sbc-pada-label',
          'text-anchor': 'middle',
        }, '1  2  3  4'), createTitle(`${perimeterIndex + 1}. ${label}`));
      } else if (corner) {
        cellGroup.append(svgElement('text', {
          x: x + CELL / 2,
          y: y + CELL / 2,
          className: 'sbc-corner-label',
          'font-size': model.language === 'hi' ? 12 : null,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        }, localizedCorner(corner.label, model.language)), createTitle(corner.description));
      } else if (inner) {
        cellGroup.append(svgElement('text', {
          x: x + CELL / 2,
          y: y + CELL / 2,
          className: 'sbc-inner-label',
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        }, inner.label), createTitle(inner.description || String(inner.label)));
      }

      group.append(cellGroup);
    }
  }

  svg.append(group);
}

function drawVedhaLines(svg, model) {
  const group = svgElement('g', { className: 'sbc-vedhas' });
  model.vedhas.forEach((vedha, index) => {
    const from = normalizeCellReference(vedha.from, model);
    const to = normalizeCellReference(vedha.to, model);
    if (!from || !to) return;
    const start = cellCenter(from.row, from.col);
    const end = cellCenter(to.row, to.col);
    const line = svgElement('line', {
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
      className: `sbc-vedha-line ${vedha.nature === 'benefic' ? 'is-benefic' : vedha.nature === 'malefic' ? 'is-malefic' : ''}`,
      'marker-end': 'url(#sbc-arrow)',
      'data-index': index,
    });
    line.append(createTitle(vedha.label || `${vedha.planet || 'Planet'} Vedha`));
    group.append(line);
  });

  const defs = svgElement('defs');
  const marker = svgElement('marker', {
    id: 'sbc-arrow',
    markerWidth: 8,
    markerHeight: 8,
    refX: 7,
    refY: 4,
    orient: 'auto',
    markerUnits: 'strokeWidth',
  });
  marker.append(svgElement('path', { d: 'M0,0 L8,4 L0,8 z', className: 'sbc-arrow-head' }));
  defs.append(marker);
  svg.append(defs, group);
}

function drawPlanetBadges(svg, model) {
  const group = svgElement('g', { className: 'sbc-planets' });
  const placements = [];

  for (const planet of model.natalPlanets) {
    const cell = cellForPlanet(planet, model);
    if (cell) placements.push({ planet, cell, type: 'natal' });
  }
  for (const planet of model.transitPlanets) {
    const cell = cellForPlanet(planet, model);
    if (cell) placements.push({ planet, cell, type: 'transit' });
  }

  const counters = new Map();
  placements.forEach(({ planet, cell, type }) => {
    const key = cellKey(cell);
    const count = counters.get(key) || 0;
    counters.set(key, count + 1);
    const center = cellCenter(cell.row, cell.col);
    const columns = 3;
    const column = count % columns;
    const row = Math.floor(count / columns);
    const x = center.x - 28 + column * 28;
    const y = center.y + 27 + row * 20;

    const badge = svgElement('g', {
      className: `sbc-planet-badge ${type} ${planet.className || PLANETS[planet.id]?.className || 'planet-generic'}`,
      transform: `translate(${x} ${y})`,
    });
    badge.append(svgElement('rect', {
      x: -12,
      y: -10,
      width: 24,
      height: 18,
      rx: 7,
      className: 'sbc-planet-badge-bg',
    }));
    badge.append(svgElement('text', {
      x: 0,
      y: -1,
      className: 'sbc-planet-badge-text',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, `${planet.short}${planet.retrograde ? 'R' : ''}`));
    badge.append(createTitle(`${type === 'transit' ? 'Transit' : 'Natal'} ${planet.name} in ${localizedNakshatra(planet.sbcNakshatra || planet.nakshatra, model.language)}`));
    group.append(badge);
  });

  svg.append(group);
}

function cellForPlanet(planet, model) {
  const explicit = model.planetCells?.[planet.id];
  if (explicit) return normalizeCellReference(explicit, model);
  const name = planet.sbcNakshatra || planet.nakshatra;
  const index = SBC_NAKSHATRAS_28.findIndex((item) => normalizeName(item) === normalizeName(name));
  return index >= 0 ? SBC_PERIMETER[index] : null;
}

function drawCenterCaption(svg, snapshot, model) {
  const center = cellCenter(4, 4);
  const score = model.gocharScore;
  const trend = model.trend || 'Neutral';
  const line1 = snapshot?.meta?.title || 'Sarvatobhadra Chakra';
  const line2 = Number.isFinite(score)
    ? `Transit strength ${score.toFixed(2)} · ${trend}`
    : trend;
  const line3 = snapshot?.meta?.displayDateTime || snapshot?.meta?.localDateTime || '';

  const group = svgElement('g', { className: 'sbc-center-caption' });
  [line1, line2, line3].filter(Boolean).forEach((line, index) => {
    group.append(svgElement('text', {
      x: center.x,
      y: center.y - 18 + index * 20,
      className: index === 0 ? 'sbc-center-title' : 'sbc-center-text',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, line));
  });
  svg.append(group);
}

function renderSarvatobhadraExplanation(model, snapshot) {
  const panel = htmlElement('aside', { className: 'sbc-explanation' });
  panel.append(htmlElement('h3', { text: model.language === 'hi' ? 'स्वचालित गोचर बल' : 'Automatic transit strength' }));

  const summary = htmlElement('div', { className: 'sbc-summary' });
  summary.append(
    htmlElement('div', { className: 'metric' }, [
      htmlElement('span', { text: model.language === 'hi' ? 'गोचर बल स्कोर' : 'Transit strength score' }),
      htmlElement('strong', { text: model.gocharScore === null ? '50.00' : model.gocharScore.toFixed(2) }),
    ]),
    htmlElement('div', { className: 'metric' }, [
      htmlElement('span', { text: model.language === 'hi' ? 'प्रवृत्ति' : 'Trend' }),
      htmlElement('strong', { text: model.trend || 'Neutral' }),
    ]),
  );
  panel.append(summary);

  if (model.ruleContributions.length > 0) {
    const table = htmlElement('table', { className: 'aat-data-table compact' });
    const head = htmlElement('thead');
    head.append(htmlElement('tr', {}, [
      htmlElement('th', { text: 'Rule' }),
      htmlElement('th', { text: 'Interpretation' }),
      htmlElement('th', { text: 'Impact' }),
    ]));
    const body = htmlElement('tbody');
    model.ruleContributions.forEach((entry) => {
      body.append(htmlElement('tr', {}, [
        htmlElement('td', { text: entry.rule || entry.id || 'Rule' }),
        htmlElement('td', { text: entry.reason || '—' }),
        htmlElement('td', { text: Number(entry.points || 0).toFixed(2) }),
      ]));
    });
    table.append(head, body);
    panel.append(table);
  }

  panel.append(createDisclaimerElement({
    language: snapshot?.meta?.disclaimerLanguage || 'en',
    className: 'aat-disclaimer sbc-disclaimer',
  }));
  return panel;
}

function renderSarvatobhadraDetails(model, snapshot) {
  const details = htmlElement('div', { className: 'sbc-details-grid' });
  details.append(
    renderPlacementDetails(model),
    renderVedhaDetails(model),
    renderSarvatobhadraExplanation(model, snapshot),
  );
  return details;
}

function renderPlacementDetails(model) {
  const section = htmlElement('section', { className: 'sbc-detail-section' });
  section.append(htmlElement('h3', {
    text: model.language === 'hi' ? 'नक्षत्र में ग्रह स्थिति' : 'Planet placements by nakshatra',
  }));

  const placements = [
    ...model.natalPlanets.map((planet) => ({ planet, type: 'natal' })),
    ...model.transitPlanets.map((planet) => ({ planet, type: 'transit' })),
  ].filter(({ planet }) => planet.sbcNakshatra || planet.nakshatra);

  if (!placements.length) {
    section.append(htmlElement('p', {
      className: 'muted',
      text: model.language === 'hi' ? 'इस समय ग्रह स्थिति उपलब्ध नहीं है।' : 'No planet placements are available for this calculation.',
    }));
    return section;
  }

  const list = htmlElement('div', { className: 'sbc-placement-list' });
  placements.forEach(({ planet, type }) => {
    const nakshatra = localizedNakshatra(planet.sbcNakshatra || planet.nakshatra, model.language);
    const typeLabel = type === 'natal'
      ? (model.language === 'hi' ? 'जन्म' : 'Natal')
      : (model.language === 'hi' ? 'गोचर' : 'Gochar');
    list.append(htmlElement('div', { className: 'sbc-placement-row' }, [
      htmlElement('span', {
        className: `sbc-placement-planet ${planet.className || PLANETS[planet.id]?.className || 'planet-generic'}`,
        text: `${planet.short}${planet.retrograde ? 'R' : ''} ${planet.name}`,
      }),
      htmlElement('span', {
        className: 'sbc-placement-position',
        text: `${typeLabel} - ${nakshatra} - ${model.language === 'hi' ? 'पाद' : 'Pada'} ${planet.pada || '-'} - ${formatDegreeInSign(planet.degreeInSign)} ${planet.sign || ''}`,
      }),
    ]));
  });
  section.append(list);
  return section;
}

function renderVedhaDetails(model) {
  const section = htmlElement('section', { className: 'sbc-detail-section' });
  section.append(htmlElement('h3', { text: model.language === 'hi' ? 'वेध विवरण' : 'Vedha details' }));

  if (!model.vedhas.length) {
    section.append(htmlElement('p', {
      className: 'muted',
      text: model.language === 'hi' ? 'इस गणना में कोई सक्रिय वेध नहीं मिला।' : 'No active Vedha was found for this calculation.',
    }));
    return section;
  }

  const list = htmlElement('div', { className: 'sbc-vedha-list' });
  model.vedhas.forEach((vedha, index) => {
    list.append(htmlElement('div', { className: `sbc-vedha-row ${vedha.nature || ''}` }, [
      htmlElement('strong', { text: vedha.label || vedha.planet || `${model.language === 'hi' ? 'वेध' : 'Vedha'} ${index + 1}` }),
      htmlElement('span', { text: `${formatCellReference(vedha.from, model)} -> ${formatCellReference(vedha.to, model)}` }),
    ]));
  });
  section.append(list);
  return section;
}

function formatCellReference(reference, model) {
  const cell = normalizeCellReference(reference, model);
  if (!cell) return String(reference || '-');
  const perimeterIndex = SBC_PERIMETER.findIndex((position) => position.row === cell.row && position.col === cell.col);
  if (perimeterIndex >= 0) return localizedNakshatra(SBC_NAKSHATRAS_28[perimeterIndex], model.language);
  const inner = model.innerCells.find((item) => item.row === cell.row && item.col === cell.col);
  return inner?.label || `${cell.row}:${cell.col}`;
}

function normalizeCellReference(reference, model) {
  if (!reference) return null;
  if (typeof reference === 'string') {
    const directMatch = reference.match(/^(\d+):(\d+)$/);
    if (directMatch) return { row: Number(directMatch[1]), col: Number(directMatch[2]) };
    const index = SBC_NAKSHATRAS_28.findIndex((name) => normalizeName(name) === normalizeName(reference));
    if (index >= 0) return SBC_PERIMETER[index];
    const inner = model.innerCells.find((cell) => normalizeName(cell.label) === normalizeName(reference));
    return inner || null;
  }
  if (Number.isInteger(reference.row) && Number.isInteger(reference.col)) return reference;
  if (reference.nakshatra) return normalizeCellReference(reference.nakshatra, model);
  if (reference.label) return normalizeCellReference(reference.label, model);
  return null;
}

function cellCenter(row, col) {
  return {
    x: GRID_ORIGIN + col * CELL + CELL / 2,
    y: GRID_ORIGIN + row * CELL + CELL / 2,
  };
}

function cellKey(cell) {
  if (typeof cell === 'string') return cell;
  return `${cell.row}:${cell.col}`;
}

function normalizeName(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9अ-ह]/gu, '');
}

function localizedNakshatra(name, language) {
  if (language !== 'hi') return name;
  const index = SBC_NAKSHATRAS_28.findIndex((item) => normalizeName(item) === normalizeName(name));
  return index >= 0 ? SBC_NAKSHATRAS_HI[index] : name;
}

function localizedCorner(label, language) {
  if (language !== 'hi') return label;
  return { N: 'वायव्य', E: 'ईशान', S: 'आग्नेय', W: 'नैऋत्य' }[label] || label;
}

function makeWrappedText(label, x, y, fontSize, maxLines) {
  const text = svgElement('text', {
    x,
    y,
    'text-anchor': 'middle',
    'dominant-baseline': 'central',
  });
  const words = String(label).split(' ');
  if (words.length === 1) {
    text.append(svgElement('tspan', { x, y }, label));
    return text;
  }
  const lines = words.length <= maxLines ? words : [words.slice(0, -1).join(' '), words.at(-1)];
  lines.forEach((line, index) => {
    text.append(svgElement('tspan', {
      x,
      y: y + (index - (lines.length - 1) / 2) * fontSize,
    }, line));
  });
  return text;
}
